import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Coins, Tv } from 'lucide-react';
import { useActiveUsersCountByCreator, useUser, useUpdateUser } from '@/hooks/useUsers';
import { UpdateUserRequest } from '@/types/model';
import UserForm from '@/components/forms/UserForm';
import Button from '@/components/ui/Button';
import CreditsModal from '@/components/ui/CreditsModal.tsx';
import { useState } from 'react';
import DeviceSessionModal from '@/components/ui/DeviceSessionModal.tsx';
import { dateWithMonthName, formatDateTime } from '@/utils/formatters.ts';
import { RemainingCreditsBadge } from '@/components/ui/RemainingCreditsBadge.tsx';
import UserCreatorLabel from '@/components/ui/UserCreatorLabel';

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);

  // Queries and mutations
  const { data: user, isLoading, isError, error } = useUser(id!);
  const updateUserMutation = useUpdateUser();
  const isAgentOrSuperagent = ['agent', 'superagent'].includes(user?.role?.code || '');
  const { data: activeUsersCountResponse, isLoading: isLoadingActiveUsersCount } = useActiveUsersCountByCreator(
    id ?? '',
    !!id && !!user && isAgentOrSuperagent
  );

  const handleCancel = () => {
    navigate(-1);
  };

  const handleUpdateUser = async (data: UpdateUserRequest) => {
    try {
      await updateUserMutation.mutateAsync({ id: id!, data });
      console.log('Usuario actualizado exitosamente');
      navigate(-1);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error; // Re-throw to be caught by the form
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-600">Cargando usuario...</div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">
                {error instanceof Error ? error.message : 'Error al cargar el usuario'}
              </div>
              <Button onClick={handleCancel}>
                Volver a Usuarios
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userInfoItems = [
    {
      key: 'created-date',
      label: 'Fecha de registro',
      value: dateWithMonthName(user.createdDate, 'No disponible')
    },
    ...(isAgentOrSuperagent
      ? [{
          key: 'users',
          label: (
            <Link
              to={`/users?createdByUserId=${user.id}`}
              className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
            >
              Usuarios
            </Link>
          ),
          value: isLoadingActiveUsersCount
            ? '...'
            : (activeUsersCountResponse?.count ?? 0).toLocaleString('es')
        }]
      : [{
          key: 'expiration-date',
          label: 'Fecha de expiración',
          value: dateWithMonthName(user.expirationDate, 'No vence')
        }]),
    {
      key: 'entries',
      label: 'Entradas',
      value: (user.role?.code == 'app' ? user.accessCount : user.signIns)?.toLocaleString('es') ?? '0'
    },
    {
      key: 'last-login',
      label: 'Último ingreso',
      value: formatDateTime(user.role?.code == 'app' ? user.lastAccessedDate : user.lastLoginDate, 'No disponible')
    },
    {
      key: 'credits',
      label: `Créditos ${['agent','superagent'].includes(user.role?.code || '') ? 'de venta' : ''}`,
      value:<RemainingCreditsBadge user={user} />
    },
    ...(isAgentOrSuperagent
      ? [{
          key: 'price-per-12-credits',
          label: 'Precio x Usuario',
          value: `${user.pricePer12CreditsCurrency === 'DOP' ? 'RD$' : 'US$'} ${(user.pricePer12Credits ?? 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        }]
      : [])
  ];

  return (
    <>
      {['app', 'agent', 'superagent'].includes(user.role?.code || '') && (
        <>
        <CreditsModal
          isOpen={creditsModalOpen}
          onClose={()=> setCreditsModalOpen(false)}
          user={user}
        />
        <DeviceSessionModal
          isOpen={deviceModalOpen}
          onClose={()=> setDeviceModalOpen(false)}
          user={user}/>
        </>
      )}
      <div className="min-h-screen bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between">
            <div>
              <Button
                variant="link"
                onClick={handleCancel}
                className="mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2"/>
                Volver a Usuarios
              </Button>
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={() => setDeviceModalOpen(true)}
                className="flex items-center gap-2"
                disabled={user.role?.code !== 'app'}
                variant="outline">
                <Tv size="16"/>
                Dispositivo
              </Button>
              <Button
                onClick={() => setCreditsModalOpen(true)}
                className="flex items-center gap-2"
                disabled={!['app', 'agent', 'superagent'].includes(user.role?.code || '') }
                variant="outline">
                <Coins size="16"/>
                Créditos
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg shadow-sm mb-4">
            <div className="flex flex-wrap gap-6 p-4 justify-between">
              {userInfoItems.map((item) => (
                <div key={item.key} className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-blue-500">
                    {item.label}
                  </span>
                  <span className="text-base font-semibold text-blue-900">
                    {item.value}
                  </span>
                  {item.key === 'created-date' && <UserCreatorLabel user={user} />}
                </div>
              ))}
            </div>
          </div>

          <UserForm
            initialData={user}
            isEdit={true}
            onSuccess={handleUpdateUser}
            onCancel={handleCancel}
            isSubmitting={updateUserMutation.isPending}
          />
        </div>
      </div>
    </>
  );
};

export default EditUser;
